/**
 * ContextDetail — the Context show page, on the shared Inspector grammar
 * (design-system §5 / frontend-display §5). It is the `/contexts/:id` route and
 * answers "where are we operating?" (upd-1.md §18.1 / §23).
 *
 * Beyond the generic Refine show it fetches `GET /api/contexts/:id`
 * (CONTRACT_V2 §3.4) — the context row plus its `context_bindings` grouped by
 * `entity_type` with counts. It lays those out in the canonical five-block
 * grammar:
 *
 *   - **Header**    — name · id · status · the two named primary actions:
 *                     **advance maturity** (`PATCH maturity_stage`, the 10-stage
 *                     ladder) and **resolve-preview** (the Context Packet).
 *   - **Overview**  — typed fields; `host_id` / `owner_user_id` as RefLinks.
 *   - **Body**      — the maturity ladder (current stage badge + "next step").
 *   - **Relations** — bound entities grouped by `entity_type` (each a RefLink),
 *                     plus the host entity.
 *   - **Audit**     — the generic `/timeline` feed (teaser until it ships).
 *
 * Read-only/advisory (D3): the detail fetch degrades to a calm error with a
 * Retry and never blocks; the maturity PATCH degrades to a toast (`[PROPOSED]`).
 */
import * as React from "react";
import { useParams } from "react-router-dom";
import { useGo, useNavigation } from "@refinedev/core";
import { Boxes, Layers, Pencil } from "@/components/ui/icon";
import { Button } from "@/components/ui/button";

import type { Context, ContextBinding } from "@aos/shared";
import { api } from "@/lib/api";
import { formatDateTime, humanize } from "@/lib/format";

import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  RelationsBlock,
  AuditBlock,
  ShowPageSkeleton,
  ShowPageError,
  type RelationGroup,
  type InspectorAction,
} from "@/components/inspector";
import { Markdown } from "@/components/app";

import { MaturityBadge, maturityNextStep } from "./MaturityBadge";
import { ContextPacketPreview } from "./ContextPacketPreview";
import { AdvanceMaturityAction } from "./AdvanceMaturityAction";
import { ControlRoomPanel } from "./ControlRoomPanel";

/** Response shape of `GET /api/contexts/:id` (CONTRACT_V2 §3.4). */
interface ContextDetailResponse {
  context: Context;
  bindings: ContextBinding[];
  bindings_by_entity_type: Record<string, ContextBinding[]>;
  counts: {
    bindings: number;
    entity_types: number;
  };
}

/** Order entity-type groups so the business-meaningful ones come first. */
const ENTITY_TYPE_ORDER = [
  "artifact",
  "package",
  "skill",
  "contour",
  "signal",
  "rule",
  "document",
  "bet",
  "frame",
  "principle",
  "metric",
  "decision",
  "objective",
  "key_result",
  "phase",
  "process",
  "user",
  "role",
  "agent",
];

/** Map a context binding `entity_type` to the Refine resource it links to. */
const ENTITY_TYPE_RESOURCE: Record<string, string> = {
  artifact: "artifacts",
  package: "packages",
  skill: "skills",
  contour: "processes",
  process: "processes",
  signal: "signals",
  rule: "rules",
  document: "documents",
  bet: "bets",
  frame: "frames",
  principle: "principles",
  metric: "metrics",
  decision: "decisions",
  objective: "objectives",
  key_result: "key-results",
  phase: "phases",
};

/** Sort entity-type keys by the curated order, unknowns alphabetically last. */
function sortEntityTypes(keys: string[]): string[] {
  return [...keys].sort((a, b) => {
    const ia = ENTITY_TYPE_ORDER.indexOf(a);
    const ib = ENTITY_TYPE_ORDER.indexOf(b);
    const ra = ia < 0 ? Number.MAX_SAFE_INTEGER : ia;
    const rb = ib < 0 ? Number.MAX_SAFE_INTEGER : ib;
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
}

/** Fetch the context detail bundle from the worker. */
function useContextDetail(id: string | undefined) {
  const [data, setData] = React.useState<ContextDetailResponse | null>(null);
  const [loading, setLoading] = React.useState(Boolean(id));
  const [error, setError] = React.useState<string | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);

  const reload = React.useCallback(() => setReloadKey((k) => k + 1), []);

  React.useEffect(() => {
    if (!id) {
      setLoading(false);
      setError("Missing context id.");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<ContextDetailResponse>(`/contexts/${encodeURIComponent(id)}/details`)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load context.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  return { data, loading, error, reload };
}

/** Build the Relations groups: bound entities by type + the host entity. */
function buildRelations(
  context: Context,
  byType: Record<string, ContextBinding[]>,
): RelationGroup[] {
  const groups: RelationGroup[] = [];

  const hostResource = context.host_type
    ? ENTITY_TYPE_RESOURCE[context.host_type]
    : undefined;
  groups.push({
    title: "Host",
    emptyMessage: "Not hosted by any entity.",
    items:
      context.host_id && hostResource
        ? [
            {
              label: humanize(context.host_type),
              resource: hostResource,
              id: context.host_id,
            },
          ]
        : context.host_id
          ? [
              {
                label: context.host_type ? humanize(context.host_type) : "Host",
                node: (
                  <span className="font-mono text-xs">{context.host_id}</span>
                ),
              },
            ]
          : [],
  });

  for (const entityType of sortEntityTypes(Object.keys(byType))) {
    const bindings = byType[entityType] ?? [];
    const resource = ENTITY_TYPE_RESOURCE[entityType];
    groups.push({
      title: `${humanize(entityType)} (${bindings.length})`,
      dividers: bindings.length > 4,
      items: bindings.map((b) => ({
        key: b.id,
        label: humanize(b.binding_type),
        icon: Boxes,
        resource,
        id: resource ? b.entity_id : undefined,
        node: resource ? undefined : (
          <span className="font-mono text-xs">{b.entity_id}</span>
        ),
      })),
    });
  }

  return groups;
}

export function ContextDetail() {
  const { id } = useParams<{ id: string }>();
  const go = useGo();
  const { editUrl } = useNavigation();
  const { data, loading, error, reload } = useContextDetail(id);

  if (loading) return <ShowPageSkeleton />;
  if (error || !data) {
    return (
      <ShowPageError message={error ?? "Context not found."} onRetry={reload} />
    );
  }

  const { context, bindings_by_entity_type, counts } = data;

  const menuActions: InspectorAction[] = [
    {
      label: "Edit",
      icon: Pencil,
      onClick: () => go({ to: editUrl("contexts", context.id) }),
    },
  ];

  return (
    <Inspector>
      <InspectorHeader
        title={context.name}
        id={context.id}
        status={context.status}
        primaryAction={
          <AdvanceMaturityAction
            contextId={context.id}
            stage={context.maturity_stage}
            onAdvanced={reload}
          />
        }
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => go({ to: `/contexts/${context.id}/packet` })}
              data-testid="context-open-packet"
            >
              <Layers className="mr-2 h-4 w-4" /> Packet
            </Button>
            <ContextPacketPreview contextId={context.id} />
          </>
        }
        menuActions={menuActions}
      />

      {context.description && context.description.trim() ? (
        <InspectorSection title="Summary">
          <Markdown source={context.description} />
        </InspectorSection>
      ) : null}

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Type">{humanize(context.context_type)}</Field>
          <Field label="Maturity">
            <MaturityBadge stage={context.maturity_stage} showOrdinal />
          </Field>
          <Field label="Priority">{context.priority}</Field>
          <Field label="Confidentiality">
            {context.confidentiality ? humanize(context.confidentiality) : "—"}
          </Field>
          <Field label="Host">
            {context.host_type ? humanize(context.host_type) : "—"}
          </Field>
          <Field label="Owner">
            <RefLink resource="users" id={context.owner_user_id} />
          </Field>
          <Field label="Boundary" full>
            {context.boundary ? (
              <span className="whitespace-pre-wrap">{context.boundary}</span>
            ) : (
              "—"
            )}
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Maturity">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-sm">
            <MaturityBadge stage={context.maturity_stage} showOrdinal />
            <span className="text-muted-foreground">
              Next step: {maturityNextStep(context.maturity_stage)}
            </span>
          </div>
          <div>
            <AdvanceMaturityAction
              contextId={context.id}
              stage={context.maturity_stage}
              variant="outline"
              onAdvanced={reload}
            />
          </div>
        </div>
      </InspectorSection>

      <InspectorSection
        title="Control Room"
        aside={
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => go({ to: `/contexts/${context.id}/control-room` })}
          >
            Open full screen
          </button>
        }
      >
        <ControlRoomPanel contextId={context.id} />
      </InspectorSection>

      <InspectorSection
        title="Relations"
        aside={
          <span className="text-xs text-muted-foreground">
            {counts.bindings} binding{counts.bindings === 1 ? "" : "s"} ·{" "}
            {counts.entity_types} type{counts.entity_types === 1 ? "" : "s"}
          </span>
        }
      >
        <RelationsBlock
          groups={buildRelations(context, bindings_by_entity_type)}
        />
      </InspectorSection>

      {/* Technical — ids + timestamps demoted to a muted tail (§4.3). */}
      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <span className="font-mono text-xs">{context.id}</span>
          </Field>
          {context.host_id ? (
            <Field label="Host id">
              <span className="font-mono text-xs">{context.host_id}</span>
            </Field>
          ) : null}
          <Field label="Review at">{formatDateTime(context.review_at)}</Field>
          <Field label="Created">{formatDateTime(context.created_at)}</Field>
          <Field label="Updated">{formatDateTime(context.updated_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="contexts" entityId={context.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default ContextDetail;
